import React, { useState, useRef, useEffect, useCallback } from "react";
import { Diagram } from "../../components/pages/editor/diagram/Diagram";
import { Sidebar } from "../../components/pages/editor/sidebar/Sidebar";
import { Navbar } from "../../components/pages/editor/navbar/Navbar";
import { MenuBar } from "../../components/pages/editor/menuBar/MenuBar";
import { Inspector } from "../../components/pages/editor/inspector/Inspector";
import "./style.css";
import { ReactFlowProvider } from "reactflow";
import SaveProgressOverlay from "../../components/global/shared/SaveProgressOverlay";
import { useDispatch, useSelector } from "react-redux";
import { updateDiagram } from "../../redux/reducers/global/diagram/diagramSlice";
import { addAlert } from "../../redux/reducers/global/alerts/alertSlice";
import { MODAL_TYPES } from "../../components/global/modal/modalTypes";
import { createEditorTemplate, deleteEditorTemplate, listEditorTemplates } from "../../api/editorTemplates";


export default function DiagramLayout() {
    const dispatch = useDispatch();
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isDirty, setDirty] = useState(false);
    const [saveStatus, setSaveStatus] = useState('ok'); // 'ok' | 'error'
    const [showSaveToast, setShowSaveToast] = useState(false);
    const markSavedRef = useRef(null);
    const selectAllRef = useRef(null);
    const cloneSelectionRef = useRef(null);
    const insertTemplateRef = useRef(null);
    const [editorTemplates, setEditorTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const handleToggleSidebar = useCallback(() => {
        setSidebarOpen((v) => !v);
    }, []);
    const autosaveIntervalMinutes = useSelector((state) => state.view.autosaveIntervalMinutes || 0);
    const isAuthModalOpen = useSelector(
        (state) => state.modal.isOpen && state.modal.modalType === MODAL_TYPES.AUTH_LOGIN
    );
    const toastTimeoutRef = useRef(null);

    const handleRegisterMarkSaved = (fn) => {
        markSavedRef.current = fn;
    };
    const handleRegisterSelectAll = (fn) => {
        selectAllRef.current = fn;
    };
    const handleRegisterCloneSelection = (fn) => {
        cloneSelectionRef.current = fn;
    };
    const handleRegisterInsertTemplate = (fn) => {
        insertTemplateRef.current = fn;
    };

    const loadEditorTemplates = useCallback(async () => {
        try {
            setTemplatesLoading(true);
            const templates = await listEditorTemplates();
            setEditorTemplates(Array.isArray(templates) ? templates : []);
        } catch (err) {
            const message = err?.data?.message || err?.message || 'No se pudieron cargar las plantillas.';
            dispatch(addAlert({
                severity: 'error',
                duration: 4000,
                message,
            }));
        } finally {
            setTemplatesLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (isAuthModalOpen) return;
        loadEditorTemplates();
    }, [isAuthModalOpen, loadEditorTemplates]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = 'Are you sure you want to leave? Save before leaving';
                return e.returnValue;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const hideSaveToast = useCallback(() => {
        setShowSaveToast(false);
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
            toastTimeoutRef.current = null;
        }
    }, []);

    const triggerSaveToast = useCallback(() => {
        hideSaveToast();
        setShowSaveToast(true);
        toastTimeoutRef.current = setTimeout(() => {
            setShowSaveToast(false);
            toastTimeoutRef.current = null;
        }, 3000);
    }, [hideSaveToast]);

    const handleSave = useCallback(async () => {
        try {
            setSaving(true);
            setSaveStatus('ok');
            setProgress(0);
            const interval = setInterval(() => {
                setProgress((p) => (p < 90 ? p + 10 : p));
            }, 300);
            await dispatch(updateDiagram()).unwrap();
            clearInterval(interval);
            setProgress(100);
            if (markSavedRef.current) {
                markSavedRef.current();
            }
            setTimeout(() => {
                setSaving(false);
                triggerSaveToast();
            }, 500);
            return true;
        } catch (err) {
            setSaving(false);
            console.error('Failed to update diagram:', err);
            setSaveStatus('error');
            hideSaveToast();
            setSaving(true);
            setProgress(100);
            const errorMessage = err?.data?.message || err?.data?.error || err?.message || 'No se pudo guardar el diagrama.';
            dispatch(addAlert({
                severity: 'error',
                duration: 4000,
                message: errorMessage,
            }));
            setTimeout(() => setSaving(false), 1200);
            return false;
        }
    }, [dispatch, markSavedRef, triggerSaveToast, hideSaveToast]);

    useEffect(() => {
        if (!autosaveIntervalMinutes || autosaveIntervalMinutes <= 0) return;
        if (isAuthModalOpen) return;
        const intervalMs = autosaveIntervalMinutes * 60 * 1000;
        const intervalId = setInterval(() => {
            if (isDirty && !saving) {
                handleSave();
            }
        }, intervalMs);
        return () => clearInterval(intervalId);
    }, [autosaveIntervalMinutes, isAuthModalOpen, isDirty, saving, handleSave]);

    const handleSelectAll = useCallback(() => {
        if (selectAllRef.current) {
            selectAllRef.current();
        }
    }, [selectAllRef]);

    const handleCloneSelection = useCallback(() => {
        if (cloneSelectionRef.current) {
            cloneSelectionRef.current();
        }
    }, [cloneSelectionRef]);

    const handleCreateTemplate = useCallback(async (templatePayload) => {
        const count = Number(templatePayload?.count) || 0;
        const defaultLabel = `Plantilla ${editorTemplates.length + 1}`;
        const label = window.prompt('Nombre de la plantilla', defaultLabel);
        if (!label || !label.trim()) {
            return null;
        }
        const savedTemplate = await createEditorTemplate({
            label: label.trim(),
            payload: {
                ...templatePayload,
                count,
            },
        });
        if (savedTemplate?.id || savedTemplate?.uuid) {
            setEditorTemplates((items) => [savedTemplate, ...(Array.isArray(items) ? items : [])]);
            dispatch(addAlert({
                severity: 'success',
                duration: 3000,
                message: 'Plantilla creada.',
            }));
        }
        return savedTemplate;
    }, [dispatch, editorTemplates.length]);

    const handleInsertTemplate = useCallback((template) => {
        if (insertTemplateRef.current) {
            insertTemplateRef.current(template);
        }
    }, []);

    const handleDeleteTemplate = useCallback(async (template) => {
        if (!template?.id) {
            return false;
        }
        try {
            await deleteEditorTemplate(template);
            setEditorTemplates((items) => (Array.isArray(items) ? items : [])
                .filter((item) => item?.id !== template.id));
            dispatch(addAlert({
                severity: 'success',
                duration: 3000,
                message: 'Plantilla eliminada.',
            }));
            return true;
        } catch (err) {
            dispatch(addAlert({
                severity: 'error',
                duration: 4000,
                message: err?.data?.message || err?.message || 'No se pudo eliminar la plantilla.',
            }));
            return false;
        }
    }, [dispatch]);

    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
                toastTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const handleGlobalSaveShortcut = (evt) => {
            if ((evt.ctrlKey || evt.metaKey) && !evt.altKey && !evt.shiftKey && (evt.key === 'g' || evt.key === 'G')) {
                const active = document.activeElement;
                const tag = active?.tagName ? active.tagName.toLowerCase() : '';
                const isTyping = tag === 'input' || tag === 'textarea' || active?.isContentEditable;
                if (isTyping) return;
                evt.preventDefault();
                handleSave();
            }
        };
        document.addEventListener('keydown', handleGlobalSaveShortcut);
        return () => document.removeEventListener('keydown', handleGlobalSaveShortcut);
    }, [handleSave]);

    return (
    <>
        <ReactFlowProvider>
            <div className="operation">
                <div className="operation__header">
                        <Navbar
                            onSave={handleSave}
                            onToggleInspector={handleToggleSidebar}
                            isInspectorOpen={isSidebarOpen}
                            onSelectAll={handleSelectAll}
                            onCloneSelection={handleCloneSelection}
                            editorTemplates={editorTemplates}
                            templatesLoading={templatesLoading}
                            onInsertTemplate={handleInsertTemplate}
                            onDeleteTemplate={handleDeleteTemplate}
                        />
                </div>
                <div className="operation__menu">
                        <MenuBar/>
                </div>
                <div className="operation__content">
                    <div className="sidebar-container">
                        <div className={`operation__sidebar ${isSidebarOpen ? '' : 'collapsed'}`}>
                            <Sidebar/>
                        </div>
                        <button
                            className={`sidebar-toggle ${isSidebarOpen ? 'open' : 'closed'}`}
                            onClick={handleToggleSidebar}
                            aria-label={isSidebarOpen ? 'Ocultar panel' : 'Mostrar panel'}
                        >
                            <span className="chevron" />
                        </button>
                    </div>
                    <div className="operation__diagram">
                        <Diagram
                            onDirty={setDirty}
                            registerMarkSaved={handleRegisterMarkSaved}
                            registerSelectAll={handleRegisterSelectAll}
                            registerCloneSelection={handleRegisterCloneSelection}
                            registerInsertTemplate={handleRegisterInsertTemplate}
                            onCreateTemplate={handleCreateTemplate}
                        />
                    </div>
                </div>
                
                {saving && <SaveProgressOverlay progress={progress} status={saveStatus} />}
                {showSaveToast && saveStatus === 'ok' && (
                    <div className="save-toast" role="status" aria-live="polite">
                        Proyecto guardado
                    </div>
                )}
            </div>
        </ReactFlowProvider>
    </>    
    )
}
